// Tiny hash router. Routes:
//   #/                  -> home (list, default) — may carry a ?query string
//   #/map               -> home with the map tab active
//   #/splash            -> splash pads (reportable spray-park catalog), may carry ?query
//   #/pool/:poolId      -> pool detail
//   #/about             -> about page
// The query string after `?` is preserved for shareable filtered views.

export type View =
  | { name: "home"; query: URLSearchParams }
  | { name: "pool"; poolId: string }
  | { name: "splash"; query: URLSearchParams }
  | { name: "about" };

export type RouteHandler = (view: View) => void;

function parseHash(): View {
  // Strip leading "#"; default to "/".
  const raw = location.hash.replace(/^#/, "") || "/";
  const [pathPart, queryPart = ""] = raw.split("?");
  const path = pathPart || "/";
  const query = new URLSearchParams(queryPart);

  if (path === "/about") {
    return { name: "about" };
  }
  if (path === "/splash") {
    // The Splash tab carries its own filter state (?open=1), like the pool views.
    return { name: "splash", query };
  }

  const poolMatch = path.match(/^\/pool\/([^/]+)$/);
  if (poolMatch) {
    return { name: "pool", poolId: decodeURIComponent(poolMatch[1]) };
  }
  // "/" and "/map" both render the home view; the active tab is carried in the
  // query (filters are preserved across these pool views).
  if (path === "/map") {
    query.set("view", "map");
    return { name: "home", query };
  }
  return { name: "home", query };
}

export function navigate(hash: string): void {
  if (location.hash === hash) {
    // Force a re-render even if the hash is unchanged.
    handleRoute();
  } else {
    location.hash = hash;
  }
}

let handler: RouteHandler = () => {};

function handleRoute(): void {
  handler(parseHash());
}

export function startRouter(onRoute: RouteHandler): void {
  handler = onRoute;
  window.addEventListener("hashchange", handleRoute);
  handleRoute();
}

export function currentView(): View {
  return parseHash();
}
